import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faMicrochip,
  faCheckCircle,
  faClock,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { partnerApi } from '../../services/api/partnerApi';
import type { PartnerUser } from '../../types/partner';

interface StatsCard {
  title: string;
  value: number | string;
  icon: typeof faUsers;
  color: string;
  description: string;
}

export default function PartnerDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    totalDevices: 0,
  });
  const [recentUsers, setRecentUsers] = useState<PartnerUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch users and devices
        const [usersResponse, devicesResponse] = await Promise.all([
          partnerApi.listUsers(1, 100, 'all'),
          partnerApi.listDevices(1, 100),
        ]);

        // Calculate stats
        const activeUsers = usersResponse.users.filter(
          (u) => u.status === 'active'
        ).length;
        const pendingUsers = usersResponse.users.filter(
          (u) => u.status === 'pending'
        ).length;

        setStats({
          totalUsers: usersResponse.pagination.total,
          activeUsers,
          pendingUsers,
          totalDevices: devicesResponse.pagination.total,
        });

        // Get recent users (last 5)
        const sortedUsers = [...usersResponse.users].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setRecentUsers(sortedUsers.slice(0, 5));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load dashboard data'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statsCards: StatsCard[] = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: faUsers,
      color: 'bg-blue-500',
      description: 'All customers',
    },
    {
      title: 'Active Users',
      value: stats.activeUsers,
      icon: faCheckCircle,
      color: 'bg-green-500',
      description: 'Registered customers',
    },
    {
      title: 'Pending Users',
      value: stats.pendingUsers,
      icon: faClock,
      color: 'bg-yellow-500',
      description: 'Awaiting registration',
    },
    {
      title: 'Total Devices',
      value: stats.totalDevices,
      icon: faMicrochip,
      color: 'bg-purple-500',
      description: 'Available devices',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Partner Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your devices and customers
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className={`${stat.color} rounded-md p-3`}>
                <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/partner/customers/invite"
            className="flex items-center justify-center px-4 py-4 border-2 border-blue-300 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="h-5 w-5 mr-2" />
            Invite Customer
          </Link>
          <Link
            to="/partner/customers"
            className="flex items-center justify-center px-4 py-4 border-2 border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <FontAwesomeIcon icon={faUsers} className="h-5 w-5 mr-2" />
            View All Customers
          </Link>
          <Link
            to="/partner/devices"
            className="flex items-center justify-center px-4 py-4 border-2 border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="h-5 w-5 mr-2" />
            Devices
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Users</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {recentUsers.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No users registered yet
            </div>
          ) : (
            recentUsers.map((user) => (
              <div key={user.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        user.status === 'active'
                          ? 'bg-green-400'
                          : 'bg-yellow-400'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.email}
                      </p>
                      <p className="text-xs text-gray-600">
                        {user.status === 'active' ? 'Active' : 'Pending'}
                        {' · '}
                        {user.role}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
